import React, { useRef, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Box,
  Paper,
  CircularProgress,
  TextField,
  Button,


  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandIcon from "@mui/icons-material/Expand";
import CompressIcon from "@mui/icons-material/Compress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Tooltip from "@mui/material/Tooltip";
import { useOrganizationDetails } from "../../api/hooks/useOrganizationDetails";
import {







  useCountries,
  useCorregimientos,

  LocationDto,
} from "../../api/hooks/location.hooks";
import { api } from "../../api/client/axiosClient";
import { ENDPOINTS } from "../../api/endpoints";
import { useQuery } from "@tanstack/react-query";
import { CustomResponseEntity } from "../../api/types/common";
import { format } from "date-fns";
import styles from "./FarmDetailsModal.module.scss";
import { useChallengesByOrganization } from "../../api/hooks/useChallenges";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useOrganizationDynamicProperties } from "../../api/hooks/useOrganizationDynamicProperties";
import { usePreloadDynamicPropertyTypes } from "../../api/hooks/useDynamicPropertyTypes";
import FormAnswersViewer from "../forms/FormAnswersViewer";
import { formatPhoneNumber } from "../../utils/phone";
import CreateEntityModal from "./CreateEntityModal";
import TrainingProgressView from "../training/TrainingProgressView";
import { FarmLocationMap } from "../map/FarmLocationMap";
import { LevelItemsList } from "./farm-details/LevelItemsList";
import { FarmLocationEditor } from "./farm-details/FarmLocationEditor";
import { FarmCropsChart } from "./farm-details/FarmCropsChart";
import { useDynamicProperties } from "./farm-details/useDynamicProperties";
import { useChallengeData } from "./farm-details/useChallengeData";
import { usePdfGeneration } from "./farm-details/usePdfGeneration";
import { useFarmLocationEditor } from "./farm-details/useFarmLocationEditor";
import { useEntityCreation } from "./farm-details/useEntityCreation";
import { getFullName } from "./farm-details/farmDetailsUtils";
import { isEndDatePreviousYear } from "../../pages/challenges/challengeUtils";

interface FarmDetailsModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}

export const FarmDetailsModal: React.FC<FarmDetailsModalProps> = ({
  open,
  onClose,
  organizationId,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch organization details
  const { data: orgDetailsResponse, isLoading: isLoadingDetails } =
    useOrganizationDetails(organizationId);
  const orgDetails = orgDetailsResponse?.data;

  // Get location details if available
  const farmLocation = orgDetails?.farmLocations[0];

  // Use farm location editor hook
  const locationEditor = useFarmLocationEditor({
    farmLocation,
    organizationId,
  });

  // Use entity creation hook
  const entityCreation = useEntityCreation({
    selectedRegionId: locationEditor.selectedRegionId,
    selectedCityId: locationEditor.selectedCityId,
  });

  // Get all cities for vereda creation modal (unfiltered)
  const { data: allCitiesResponse } = useQuery({
    queryKey: ["all-cities"],
    queryFn: () =>
      api.get<CustomResponseEntity<LocationDto[]>>(ENDPOINTS.cities.base()),
    enabled: entityCreation.createModalOpen && entityCreation.createModalType === "vereda",
  });

  const allCities = allCitiesResponse?.data || [];

  const { data: countriesResponse } = useCountries();
  const { data: corregimientosResponse } = useCorregimientos();
  const countries = countriesResponse?.data || [];
  const corregimientos = corregimientosResponse?.data || [];

  // Get own (parent) organization id
  const { selectedOrganization } = useOrganization();
  const ownOrgId = selectedOrganization?.id;

  // Note: Authorization variables kept for potential future use
  // const { hasPrivilege, loading: authLoading } = useAuthorization();
  // const canModify = hasPrivilege('ADMIN_ACCESS') || hasPrivilege('SUPER_ADMIN');

  // Fetch dynamic properties for the selected organization
  const { data: dynamicPropertyTypes } = usePreloadDynamicPropertyTypes();
  const { data: organizationDynamicProperties } =
    useOrganizationDynamicProperties(selectedOrganization?.id || "");

  // Use dynamic properties hook
  const dynamicProperties = useDynamicProperties({
    organizationDynamicProperties,
    dynamicPropertyTypes: dynamicPropertyTypes as any,
  });

  // Fetch challenge data for the specific organization being viewed
  const { data: challengesTableData, isLoading: isLoadingChallenges } =
    useChallengesByOrganization(ownOrgId || "", organizationId, {
      enabled: !!ownOrgId && !!organizationId,
    });

  const latestUpdateDate = useMemo(() => {
    if (!orgDetails) return null;

    const dates: (string | null | undefined)[] = [
      orgDetails.updatedAt,
      ...orgDetails.farmLocations.map((loc) => (loc as any).updatedAt),
      ...orgDetails.farmSocioeconomics.map((socio) => (socio as any).updatedAt),
    ];

    if (challengesTableData?.data?.challenges) {
      const challenge = challengesTableData.data.challenges.find(
        (c: any) => c.inspectedOrgId === organizationId,
      );
      if (challenge) {
        challenge.inspectionsProgramsVersionsItems.forEach((item: any) => {
          if (item.updatedAt) {
            dates.push(item.updatedAt);
          }
        });
      }
    }

    const validDates = dates.filter(Boolean);
    if (validDates.length === 0) return null;

    return new Date(
      Math.max(...validDates.map((d) => new Date(d as string).getTime())),
    );
  }, [orgDetails, challengesTableData, organizationId]);

  // Get phone number from challenges table for this organization
  const inspectedUserPhones = React.useMemo(() => {
    if (!challengesTableData?.data?.challenges) return undefined;
    const challenge = challengesTableData.data.challenges.find(
      (c: any) => c.inspectedOrgId === organizationId,
    );
    return challenge?.inspectedUserPhones;
  }, [challengesTableData, organizationId]);

  // Use challenge data hook
  const challengeData = useChallengeData({
    challengesTableData: challengesTableData as any,
    organizationId,
    ownOrgId,
  });

  // State for expand/collapse all accordions in Resumen de Tareas
  const [allTasksExpanded, setAllTasksExpanded] = useState<boolean>(false);
  const [expandToggleKey, setExpandToggleKey] = useState(0);

  // State for showing sensitive data in PDF
  const [showSensitiveData, setShowSensitiveData] = useState<boolean>(false);

  // Use PDF generation hook
  const { isGeneratingPdf, handleDownloadPDF } = usePdfGeneration({
    orgDetails,
    cityName: locationEditor.cityName,
    corregimientoName: locationEditor.corregimientoName,
    veredaName: locationEditor.veredaName,
    regionName: locationEditor.regionName,
    farmLocation,
    sortedApprovedLevels: challengeData.sortedApprovedLevels,
    inspectedUserPhones,
    ownOrgId,
    organizationId,
    organizationDynamicProperties,
    dynamicPropertyTypes: dynamicPropertyTypes as any,
    pdfHeaderTitle: dynamicProperties.pdfHeaderTitle,
    pdfHeaderSubtitle: dynamicProperties.pdfHeaderSubtitle,
    pdfSectionChallenges: dynamicProperties.pdfChallengesVisible,
    pdfSectionTrainings: dynamicProperties.pdfTrainingsVisible,
    pdfSectionAttachments: dynamicProperties.pdfAttachmentsVisible,
    formsMap: challengeData.formsMap,
    answersMap: challengeData.answersMap,
    showSensitiveData,
  });

  const handleToggleSensitiveData = () => {
    setShowSensitiveData((prev) => !prev);
  };

  const handleDownloadPDFWithReset = async () => {
    await handleDownloadPDF();
    setShowSensitiveData(false);
  };

  const handleToggleAllTasks = () => {
    setAllTasksExpanded(!allTasksExpanded);
    setExpandToggleKey((prev) => prev + 1);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className={styles.dialog}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
          {dynamicProperties.title}
        </Typography>
        <div className={styles.dialogActions}>
          <Tooltip
            title={showSensitiveData ? "Ocultar datos sensibles" : "Mostrar datos sensibles"}
            arrow
          >
            <IconButton
              onClick={handleToggleSensitiveData}
              className={styles.privacyToggle}
              size="large"
              color={showSensitiveData ? "primary" : "default"}
            >
              {showSensitiveData ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={
              isGeneratingPdf ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleDownloadPDFWithReset}
            className={styles.downloadButton}
            disabled={isGeneratingPdf}
            disableElevation
          >
            {isGeneratingPdf ? "Generando..." : "Descargar PDF"}
          </Button>
          <IconButton
            aria-label="cerrar"
            onClick={onClose}
            className={styles.closeButton}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {isLoadingDetails ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={600}
          >
            <CircularProgress />
          </Box>
        ) : !orgDetails ? (
          <Typography color="error">Error loading farm details</Typography>
        ) : (
          <div ref={contentRef}>
            <Grid container spacing={3}>
              {/* Left side - Farm information */}
              <Grid item xs={12} md={8}>
                <Paper className={styles.section}>
                  {/* Farm Header */}
                  <Box className={styles.farmHeader}>
                    <Typography className={styles.farmName}>
                      {orgDetails.name}
                    </Typography>
                    <Typography className={styles.updateDate}>
                      Última actualización:{" "}
                      {latestUpdateDate
                        ? format(latestUpdateDate, "MMM dd, yyyy")
                        : "N/A"}
                    </Typography>
                  </Box>

                  {/* Datos Generales Section */}
                  <Box className={styles.fieldGroup}>
                    <Typography className={styles.subsectionTitle}>
                      Datos generales
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <TextField
                          label="Nombre y Apellido"
                          value={getFullName(
                            orgDetails.organizationsUsers[0]?.user,
                          )}
                          fullWidth
                          disabled
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Nombre de la Finca"
                          value={orgDetails.name}
                          fullWidth
                          disabled
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label={orgDetails.idType?.name || "Cédula"}
                          value={orgDetails.idNumber}
                          fullWidth
                          disabled
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Contacto"
                          value={
                            inspectedUserPhones &&
                            inspectedUserPhones.length > 0
                              ? inspectedUserPhones
                                  .map(formatPhoneNumber)
                                  .join(", ")
                              : orgDetails.organizationsUsers[0]?.user?.phone
                                ? formatPhoneNumber(
                                    orgDetails.organizationsUsers[0]?.user
                                      ?.phone,
                                  )
                                : "N/A"
                          }
                          fullWidth
                          disabled
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Location Section */}
                  <Box sx={{ mt: 3 }}>
                    <FarmLocationEditor
                        isEditing={locationEditor.isEditingLocation}
                        regionName={locationEditor.regionName}
                        cityName={locationEditor.cityName}
                        veredaName={locationEditor.veredaName}
                        selectedRegionId={locationEditor.selectedRegionId}
                        selectedCityId={locationEditor.selectedCityId}
                        selectedVeredaId={locationEditor.selectedVeredaId}
                        regions={locationEditor.regions}
                        cities={locationEditor.cities}
                        veredas={locationEditor.veredas}
                        isUpdatingLocation={locationEditor.isUpdatingLocation}
                        onRegionChange={locationEditor.setSelectedRegionId}
                        onCityChange={locationEditor.setSelectedCityId}
                        onVeredaChange={locationEditor.setSelectedVeredaId}
                        onCreateRegion={() => entityCreation.handleOpenCreateModal("region")}
                        onCreateCity={() => entityCreation.handleOpenCreateModal("city")}
                        onCreateVereda={() => entityCreation.handleOpenCreateModal("vereda")}
                        onSave={locationEditor.handleSaveFarmLocationChanges}
                        onCancel={() => {
                          locationEditor.setIsEditingLocation(false);
                          locationEditor.setSelectedRegionId(locationEditor.regionId || "");
                          locationEditor.setSelectedCityId(locationEditor.cityId || "");
                          locationEditor.setSelectedVeredaId(locationEditor.veredaId || "");
                        }}
                      />
                    </Box>
                  {/* <Box mt={3}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Información general
                    </Typography>
                  </Box> */}

                  {/* {farmLocation && (
                    <Box mt={3}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Datos geográficos
                      </Typography>
                      <Grid container spacing={3}>
                        {cityName && (
                          <Grid item xs={6}>
                            <TextField
                              label="Ciudad"
                              value={cityName}
                              fullWidth
                              disabled
                              variant="outlined"
                            />
                          </Grid>
                        )}
                        {corregimientoName && (
                          <Grid item xs={6}>
                            <TextField
                              label="Corregimiento"
                              value={corregimientoName}
                              fullWidth
                              disabled
                              variant="outlined"
                            />
                          </Grid>
                        )}
                        {veredaName && (
                          <Grid item xs={6}>
                            <TextField
                              label="Vereda"
                              value={veredaName}
                              fullWidth
                              disabled
                              variant="outlined"
                            />
                          </Grid>
                        )}
                        <Grid item xs={6}>
                          <TextField
                            label="Área"
                            value={`${(farmLocation as any).area} Ha`}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Altura"
                            value={`${farmLocation.elevation} m`}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Coordenadas"
                            value={`${farmLocation.latitude}°N, ${farmLocation.longitude}°W`}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )} */}

                  {/* Workers Information */}
                  {/* {orgDetails.farmSocioeconomics[0] && (
                    <Box mt={3}>
                      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                        <GroupsIcon /> Información Socioeconómica
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <TextField
                            label="Trabajadores Hombres Tiempo Completo"
                            value={orgDetails.farmSocioeconomics[0].malesFullTimeWorkers}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Trabajadores Hombres Temporales"
                            value={orgDetails.farmSocioeconomics[0].malesTemporalWorkers}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Trabajadoras Mujeres Tiempo Completo"
                            value={orgDetails.farmSocioeconomics[0].femaleFullTimeWorkers}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Trabajadoras Mujeres Temporales"
                            value={orgDetails.farmSocioeconomics[0].femaleTemporalWorkers}
                            fullWidth
                            disabled
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                            Total Trabajadores: {totalWorkers}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )} */}

                  {/* Farm Managers */}
                  {/* {orgDetails.organizationsUsers.length > 0 && (
                    <Box mt={3}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon /> Administradores
                      </Typography>
                      <Grid container spacing={2}>
                        {orgDetails.organizationsUsers.map((user) => (
                          <Grid item xs={12} key={user.id}>
                            <Box p={2} sx={{ backgroundColor: 'background.default', borderRadius: 1 }}>
                              <Typography variant="subtitle2">{user.user.name}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {user.description}
                                {user.isOwner && (
                                  <Chip
                                    label="Owner"
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )} */}

                  {/* Inspections Information */}
                  {/* {orgDetails.inspectionsReceived && orgDetails.inspectionsReceived.length > 0 && (
                    <Box mt={3}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon /> Inspecciones
                      </Typography>
                      <Grid container spacing={2}>
                        {orgDetails.inspectionsReceived.map((inspection: Inspection) => (
                          <Grid item xs={12} key={inspection.id}>
                            <Box
                              p={2}
                              sx={{
                                backgroundColor: 'background.default',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2">
                                  {inspection.name}
                                  {!inspection.closeDate && (
                                    <Chip
                                      label="En Progreso"
                                      size="small"
                                      color="warning"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {format(new Date(inspection.startDate), 'MMM dd, yyyy')} - {format(new Date(inspection.endDate), 'MMM dd, yyyy')}
                                </Typography>
                              </Box>

                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                {inspection.description}
                              </Typography>

                              {inspection.inspectionsPrograms && inspection.inspectionsPrograms.map((program: InspectionProgram) => (
                                <Box key={program.id} mt={1}>
                                  <Chip
                                    label={program.programVersion?.name || 'Program pending'}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                  />
                                </Box>
                              ))}

                              {inspection.closeDate && (
                                <Typography variant="caption" display="block" mt={1} color="success.main">
                                  Completada el: {format(new Date(inspection.closeDate), 'MMM dd, yyyy')}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )} */}
                </Paper>
              </Grid>

              {/* Right side - Logo and distribution */}
              <Grid item xs={12} md={4}>
                {orgDetails.logo &&
                  !(
                    farmLocation &&
                    farmLocation.latitude &&
                    farmLocation.longitude
                  ) && (
                    <Paper className={styles.sidebarCard}>
                      <img
                        src={orgDetails.logo}
                        alt={`${orgDetails.name} logo`}
                        className={styles.logo}
                      />
                    </Paper>
                  )}

                {/* Location Map */}
                {farmLocation &&
                  farmLocation.latitude &&
                  farmLocation.longitude && (
                    <Paper className={styles.sidebarCard} sx={{ padding: 0, overflow: "hidden" }}>
                      <Box sx={{ p: 2 }}>
                        <FarmLocationMap
                          latitude={farmLocation.latitude}
                          longitude={farmLocation.longitude}
                          area={(farmLocation as any).area}
                        />
                      </Box>
                    </Paper>
                  )}

                {/* Crops Information */}
                {orgDetails.farmCrops && orgDetails.farmCrops.length > 0 && (
                  <FarmCropsChart farmCrops={orgDetails.farmCrops} />
                )}
              </Grid>
            </Grid>

            {/* Resumen de retos Section - Full Width */}
            {dynamicProperties.challengesVisible && (
              <Box mt={4}>
                {isLoadingChallenges ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={200}
                  >
                    <CircularProgress />
                  </Box>
                ) : Object.keys(challengeData.allItemsByLevel).length === 0 ? (
                  <Paper className={styles.section}>
                    <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2, color: "var(--primary)" }}>
                      Datos de la Finca
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      No hay retos para esta finca.
                    </Typography>
                  </Paper>
                ) : (
                  <Box className={styles.cardGroup}>
                    {/* Section Header */}
                    <Box className={styles.sectionHeader}>
                      <Typography className={styles.sectionTitle}>
                        Datos de la Finca
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={allTasksExpanded ? <CompressIcon /> : <ExpandIcon />}
                        onClick={handleToggleAllTasks}
                        className={styles.expandButton}
                      >
                        {allTasksExpanded ? "Colapsar" : "Expandir"}
                      </Button>
                    </Box>
                    {/* Level Sections */}
                    {challengeData.sortedLevels.map(([levelName, pvis], _index) => {
                      return (
                        <Box key={levelName} className={styles.levelSection}>
                          <Typography className={styles.levelTitle}>
                            {levelName}
                          </Typography>
                          <LevelItemsList
                            levelName={levelName}
                            pvis={pvis}
                            farmItemsMap={challengeData.farmItemsMap}
                            showTitle={false}
                            formsMap={challengeData.formsMap}
                            answersMap={challengeData.answersMap}
                            allExpanded={allTasksExpanded}
                            expandToggleKey={expandToggleKey}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* Training Progress Section */}
            {dynamicProperties.trainingsVisible && (
              <TrainingProgressView organizationId={organizationId} />
            )}

            {/* Approved Items Attachments - Moved to end */}
            {dynamicProperties.attachmentsVisible &&
              Object.keys(challengeData.approvedItemsByLevel).length > 0 && (
                <Box mt={4}>
                  <Box className={styles.cardGroup}>
                    {/* Section Header */}
                    <Box className={styles.sectionHeader}>
                      <Typography className={styles.sectionTitle}>
                        Adjuntos
                      </Typography>
                    </Box>

                    {challengeData.sortedApprovedLevels.map(([levelName, items], _index) => (
                      <Box key={levelName} className={styles.levelSection}>
                        <Typography className={styles.levelTitle} sx={{ textTransform: "capitalize" }}>
                          {levelName.toLowerCase()}
                        </Typography>
                        {items.map((item) => {
                          const isApprovedButEmptyValue =
                            item.isApproved &&
                            isEndDatePreviousYear(item.endDate);
                          let boxBgColor = "var(--success-light)";
                          let boxBorderColor = "var(--success-light)";
                          let boxTextColor = "#fff";

                          if (isApprovedButEmptyValue) {
                            boxBgColor = "var(--info-light)";
                            boxBorderColor = "var(--info-light)";
                            boxTextColor = "var(--info-dark)";
                          }

                          return (
                            <Box
                              key={item.id}
                              className={styles.attachmentCard}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={2}
                                mb={2}
                              >
                                <Box
                                  sx={{
                                    minWidth: 40,
                                    width: "auto",
                                    padding: "0 12px",
                                    height: 40,
                                    borderRadius: "10px",
                                    backgroundColor: boxBgColor,
                                    border: `2px solid ${boxBorderColor}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    color: boxTextColor,
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.pvi.cpNumber.split(".").pop()}
                                </Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600, color: "var(--primary)" }}
                                >
                                  {item.pvi.name}
                                </Typography>
                              </Box>
                              <FormAnswersViewer
                                form={item.pvi.formId ? challengeData.formsMap[item.pvi.formId] : undefined}
                                answers={item.pvi.formId ? (challengeData.answersMap[item.id] || []) : undefined}
                                formTemplateJson={
                                  item.pvi.measurementTemplate
                                }
                                formAnswersJson={item.value}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
          </div>
        )}
      </DialogContent>

      {/* Create Entity Modal */}
      <CreateEntityModal
        open={entityCreation.createModalOpen}
        onClose={() => entityCreation.setCreateModalOpen(false)}
        title={
          entityCreation.createModalType === "region"
            ? "Departamento"
            : entityCreation.createModalType === "city"
              ? "Municipio"
              : "Vereda"
        }
        entityType={entityCreation.createModalType}
        onSave={entityCreation.handleSaveEntity}
        loading={entityCreation.createModalLoading}
        externalFields={
          entityCreation.createModalType === "region"
            ? { countryId: entityCreation.selectedCountryId }
            : entityCreation.createModalType === "city"
              ? { regionId: entityCreation.selectedRegionForCity }
              : entityCreation.createModalType === "vereda"
                ? {
                    cityId: entityCreation.selectedCityForVereda,
                    corregimientoId: entityCreation.selectedCorregimientoForVereda,
                  }
                : {}
        }
        additionalFields={
          entityCreation.createModalType === "region" ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>País</InputLabel>
              <Select
                value={entityCreation.selectedCountryId}
                onChange={(e) => entityCreation.setSelectedCountryId(e.target.value)}
                label="País"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : entityCreation.createModalType === "city" ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>Departamento</InputLabel>
              <Select
                value={entityCreation.selectedRegionForCity}
                onChange={(e) => entityCreation.setSelectedRegionForCity(e.target.value)}
                label="Departamento"
              >
                {locationEditor.regions.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    {region.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : entityCreation.createModalType === "vereda" ? (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Ciudad</InputLabel>
                <Select
                  value={entityCreation.selectedCityForVereda}
                  onChange={(e) => entityCreation.setSelectedCityForVereda(e.target.value)}
                  label="Ciudad"
                >
                  {allCities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Corregimiento</InputLabel>
                <Select
                  value={entityCreation.selectedCorregimientoForVereda}
                  onChange={(e) =>
                    entityCreation.setSelectedCorregimientoForVereda(e.target.value)
                  }
                  label="Corregimiento"
                >
                  {corregimientos.map((corregimiento) => (
                    <MenuItem key={corregimiento.id} value={corregimiento.id}>
                      {corregimiento.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null
        }
      />
    </Dialog>
  );
};

export default FarmDetailsModal;
